/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

/**
 * Generic Airtable REST client.
 *
 * One reusable paginated fetcher; specialized fetchers (Gold Instructors,
 * Training Partners, Service Partners) build their domain objects on top.
 *
 * ## Personal Access Token (PAT) Setup
 *
 * Each consumer of this client must supply an Airtable PAT scoped to the
 * base it targets. PATs are provisioned in vault and injected via
 * ExternalSecrets (see each website's `.env.local.ctemplate` and
 * `deploy/charts/values.*.yaml`).
 *
 * - Gold Instructors:   `AIRTABLE_INSTRUCTORS_PAT`
 * - Training Partners:  `AIRTABLE_TRAINING_PARTNER_PAT`
 * - Service Partners:   (vault key TBD when that consumer ships)
 *
 * ### How to create a new token:
 * 1. Go to https://airtable.com/create/tokens
 * 2. Click "Create new token"
 * 3. Scopes: `data.records:read`, `schema.bases:read`
 * 4. Access: add the base the token will read
 * 5. Create + copy the value (starts with `pat...`)
 *
 * ### Rate limits
 * - 5 requests/second per base
 * - 50 requests/second per PAT across all bases
 */

const AIRTABLE_API_BASE = "https://api.airtable.com/v0";
const DEFAULT_PAGE_SIZE = 100;
// 1 hour. Must stay well below Airtable's ~2h signed-URL TTL on attachment
// thumbnails — bumping this above ~6000s silently breaks partner logos
// because the cached response will hold an expired image URL.
export const DEFAULT_REVALIDATE_SECONDS = 3600;
const REQUEST_TIMEOUT_MS = 8000;
const MAX_RETRY_ON_429 = 2;
const RETRY_AFTER_FALLBACK_MS = 1000;
// Cap Retry-After to bound the total fetch budget — a hostile or
// misconfigured upstream `Retry-After: 600` would otherwise block the
// server-component render for 10 minutes per attempt.
const RETRY_AFTER_CAP_MS = 5000;
// Hard safety cap on total records / pagination loop. Either limit hitting
// is a regression (filter dropped, schema bloat) — log loudly and truncate
// rather than silently OOM the server. The directory renders with the
// partial set (best-effort) so a misconfigured filter doesn't 5xx the page;
// the `console.error` is the operator signal to investigate.
const MAX_RECORDS = 10_000;
const MAX_PAGES = 100;
// Wall-clock budget across all pages of a single fetch. Bounds the worst-case
// render time even when sustained 429s would otherwise let pagination block
// for ~MAX_PAGES × REQUEST_TIMEOUT_MS. Exported so a regression test pins
// the invariant ("must stay under Next 30s server-component cap, with room
// to spare for the surrounding Hygraph fetch in Promise.all").
export const PAGINATION_BUDGET_MS = 15_000;

export class AirtablePatMissingError extends Error {
	constructor(consumerLabel: string, envVarName: string) {
		super(
			`[${consumerLabel}] ${envVarName} is not set. Verify the vault key exists and chart values are updated for this environment.`,
		);
		this.name = "AirtablePatMissingError";
	}
}

/**
 * Triage shape attached to `AirtableFirstPageError.cause` so APM and tests
 * can distinguish a network timeout from a rate-limit exhaustion from a
 * generic 5xx without re-instrumenting the client.
 */
export interface AirtableFirstPageCause {
	reason: "timeout" | "network" | "http" | "rateLimitExhausted" | "budgetExhausted" | "parseError";
	status?: number;
	attempts: number;
}

/**
 * Thrown when the very first page of an Airtable fetch fails (HTTP error,
 * network failure, or timeout). The caller should NOT cache an empty result
 * — surface the error so the consumer page can render a 5xx (and let APM
 * page someone) rather than silently rendering an empty directory for the
 * next revalidate window.
 *
 * Mid-pagination failures are different: pages already collected are
 * returned best-effort.
 */
export class AirtableFirstPageError extends Error {
	readonly reason: AirtableFirstPageCause["reason"] | undefined;
	readonly status: number | undefined;
	readonly attempts: number | undefined;
	constructor(consumerLabel: string, cause?: AirtableFirstPageCause) {
		const suffix = cause
			? ` (${cause.reason}${cause.status ? ` ${cause.status}` : ""}, ${cause.attempts} attempts)`
			: "";
		super(`[${consumerLabel}] Airtable first-page fetch failed — refusing to cache empty result${suffix}`);
		this.name = "AirtableFirstPageError";
		this.reason = cause?.reason;
		this.status = cause?.status;
		this.attempts = cause?.attempts;
		if (cause !== undefined) {
			(this as { cause?: unknown }).cause = cause;
		}
	}
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export interface AirtableRecord<TFields> {
	id: string;
	createdTime: string;
	fields: TFields;
}

interface AirtableResponse<TFields> {
	records: AirtableRecord<TFields>[];
	offset?: string;
}

export interface FetchAirtableRecordsOptions {
	/** Base ID (e.g. `appTXNGNPznnzwjC0`). */
	baseId: string;
	/** Table name within the base. */
	tableName: string;
	/** Personal Access Token, sourced from the consumer's env var. */
	pat: string | undefined;
	/** Optional Airtable formula. e.g. `"{2026 Badge}='GOLD'"`. */
	filterByFormula?: string;
	/** Page size — Airtable caps at 100. */
	pageSize?: number;
	/** Cache duration in seconds for Next.js `fetch` revalidation. */
	revalidateSeconds?: number;
	/** Identifier used in error messages and logs (e.g. `"UAI Directory"`). */
	consumerLabel: string;
	/** Env var name reported in the missing-PAT error message. */
	envVarName: string;
}

/**
 * One paginated GET to Airtable with retry on 429 and an 8s timeout.
 *
 * Returns the parsed JSON page or `null` if the request ultimately failed
 * (non-2xx with retries exhausted, timeout, or network error). Callers
 * decide whether `null` means "fail loud" (first page) or "keep partial
 * results" (mid-pagination).
 *
 * Cache semantics: the first page (no `offset`) is Next-cached for
 * `revalidateSeconds`. Offset pages get `cache: "no-store"` — Airtable
 * offset cursors are stateful server-side and expire on their own clock;
 * caching them produces deterministic 422s the moment the upstream cursor
 * expires faster than the Next cache window.
 */
/**
 * Discriminated result for `fetchAirtablePage` — when the page fails, the
 * `reason` tag lets the caller propagate triage info (timeout vs HTTP vs
 * 429-exhaustion) into `AirtableFirstPageError.cause`.
 */
type AirtablePageResult<TFields> =
	| { ok: true; data: AirtableResponse<TFields> }
	| {
			ok: false;
			reason: "timeout" | "network" | "http" | "rateLimitExhausted" | "budgetExhausted" | "parseError";
			status?: number;
			attempts: number;
	  };

async function fetchAirtablePage<TFields>(
	url: string,
	pat: string,
	revalidateSeconds: number,
	consumerLabel: string,
	isOffsetPage: boolean,
	getRemainingBudgetMs: () => number,
): Promise<AirtablePageResult<TFields>> {
	// Threshold below which a "remaining budget" is effectively exhausted —
	// firing a fetch with a sub-`MIN_BUDGET_HEADROOM_MS` AbortSignal is
	// almost guaranteed to abort and produces a misleading `reason: "timeout"`
	// in the error cause. Treat it as `budgetExhausted` so APM gets accurate
	// triage signal.
	const MIN_BUDGET_HEADROOM_MS = 500;
	for (let attempt = 0; attempt <= MAX_RETRY_ON_429; attempt++) {
		// Recompute remaining budget on EVERY iteration — captured-once would
		// let `MAX_RETRY_ON_429 × RETRY_AFTER_CAP_MS = 10s` of sleeps stack on
		// top of the per-attempt timeout, blowing past the documented wall-
		// clock budget. Each retry re-derives from the call-site clock.
		const remainingBudgetMs = getRemainingBudgetMs();
		if (remainingBudgetMs < MIN_BUDGET_HEADROOM_MS) {
			console.warn(`[${consumerLabel}] Airtable pagination budget exhausted mid-page`);
			return { ok: false, reason: "budgetExhausted", attempts: attempt };
		}
		const attemptTimeoutMs = Math.min(REQUEST_TIMEOUT_MS, remainingBudgetMs);

		// Next.js augments `RequestInit` with a `next` field for revalidation;
		// the service stays Next-agnostic at the type level via a cast.
		const init: RequestInit & { next?: { revalidate?: number } } = {
			headers: { Authorization: `Bearer ${pat}` },
			...(isOffsetPage ? { cache: "no-store" as const } : { next: { revalidate: revalidateSeconds } }),
			signal: AbortSignal.timeout(attemptTimeoutMs),
		};

		let response: Response;
		try {
			response = await fetch(url, init);
		} catch (error) {
			const isAbort = error instanceof Error && error.name === "TimeoutError";
			// Log only the message — the raw error object can include the request
			// init (with the bearer token) in some Node versions.
			const message = error instanceof Error ? error.message : String(error);
			console.error(`[${consumerLabel}] Airtable fetch ${isAbort ? "timed out" : "failed"}: ${message}`);
			return { ok: false, reason: isAbort ? "timeout" : "network", attempts: attempt + 1 };
		}

		if (response.ok) {
			// Parse the response body inside its own try/catch so a 2xx with a
			// truncated / malformed / empty body (rare upstream-proxy edge cases)
			// surfaces as a typed `parseError` instead of bubbling past the
			// fail-loud first-page contract into the generic fetchCards catch
			// (which would silently empty-cache the directory for the full
			// revalidate window).
			//
			// 204 / `content-length: 0` short-circuits — calling `.json()` on
			// an empty body throws "Unexpected end of JSON input" with no
			// indication of the upstream's intent.
			if (response.status === 204 || response.headers.get("content-length") === "0") {
				console.error(`[${consumerLabel}] Airtable returned ${response.status} with empty body`);
				return { ok: false, reason: "parseError", status: response.status, attempts: attempt + 1 };
			}
			try {
				const data = (await response.json()) as AirtableResponse<TFields>;
				return { ok: true, data };
			} catch (parseError) {
				const message = parseError instanceof Error ? parseError.message : String(parseError);
				console.error(`[${consumerLabel}] Airtable response parse failed: ${message}`);
				return { ok: false, reason: "parseError", status: response.status, attempts: attempt + 1 };
			}
		}

		if (response.status === 429 && attempt < MAX_RETRY_ON_429) {
			const retryAfterHeader = response.headers.get("Retry-After");
			// Retry-After can be either a number of seconds (RFC-compliant integer)
			// or an HTTP-date — only the integer form is meaningful here. Anything
			// non-finite (parseInt of an HTTP-date returns NaN) OR non-positive
			// (defensive: a misconfigured upstream proxy could send `-5`) falls
			// back to the linear backoff. Clamp the upstream value so a hostile
			// `Retry-After: 600` can't block the render for minutes.
			const parsedSeconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : NaN;
			const rawRetryAfterMs =
				Number.isFinite(parsedSeconds) && parsedSeconds > 0
					? parsedSeconds * 1000
					: RETRY_AFTER_FALLBACK_MS * (attempt + 1);
			// Clamp to both the global cap AND the remaining wall-clock budget
			// — if the budget is exhausted, fall through to fail rather than
			// waste the rest of the page on a sleep we'll abort anyway.
			const retryAfterMs = Math.min(rawRetryAfterMs, RETRY_AFTER_CAP_MS, Math.max(remainingBudgetMs, 0));
			if (retryAfterMs <= 0) {
				console.warn(`[${consumerLabel}] Airtable 429 but pagination budget exhausted; skipping retry`);
				return { ok: false, reason: "budgetExhausted", status: 429, attempts: attempt + 1 };
			}
			console.warn(
				`[${consumerLabel}] Airtable 429; retrying in ${retryAfterMs}ms (attempt ${attempt + 1}/${MAX_RETRY_ON_429})`,
			);
			await sleep(retryAfterMs);
			continue;
		}

		console.error(`[${consumerLabel}] Airtable API error: ${response.status} ${response.statusText}`);
		// 429 with retries exhausted → rateLimitExhausted; any other non-2xx → http.
		return {
			ok: false,
			reason: response.status === 429 ? "rateLimitExhausted" : "http",
			status: response.status,
			attempts: attempt + 1,
		};
	}
	return { ok: false, reason: "rateLimitExhausted", attempts: MAX_RETRY_ON_429 + 1 };
}

/**
 * Fetch every record from an Airtable table, following pagination cursors.
 *
 * Error contract:
 * - `AirtablePatMissingError` — env var not set. Fail-loud so vault
 *   misconfiguration doesn't silently render an empty page.
 * - `AirtableFirstPageError` — the first page (no `offset`) failed. Caller
 *   should surface a 5xx instead of caching an empty result.
 * - Otherwise: returns whatever pages were collected before a mid-pagination
 *   failure (best-effort), so a single bad page doesn't blank the whole
 *   directory. Pages already in the Next cache stay there.
 *
 * Bounded by:
 * - `REQUEST_TIMEOUT_MS` (8s) per page
 * - `MAX_PAGES` (100) / `MAX_RECORDS` (10_000) safety caps
 * - `PAGINATION_BUDGET_MS` (15s) total wall-clock across all pages
 */
export async function fetchAllAirtableRecords<TFields>({
	baseId,
	tableName,
	pat,
	filterByFormula,
	pageSize = DEFAULT_PAGE_SIZE,
	revalidateSeconds = DEFAULT_REVALIDATE_SECONDS,
	consumerLabel,
	envVarName,
}: FetchAirtableRecordsOptions): Promise<AirtableRecord<TFields>[]> {
	if (!pat) {
		throw new AirtablePatMissingError(consumerLabel, envVarName);
	}

	const allRecords: AirtableRecord<TFields>[] = [];
	const startedAt = Date.now();
	let offset: string | undefined;
	let page = 0;

	do {
		page++;
		if (page > MAX_PAGES || allRecords.length > MAX_RECORDS) {
			console.error(
				`[${consumerLabel}] Airtable pagination cap hit (pages=${page}, records=${allRecords.length}); aborting to avoid unbounded loop`,
			);
			break;
		}

		const elapsed = Date.now() - startedAt;
		if (elapsed > PAGINATION_BUDGET_MS) {
			console.warn(
				`[${consumerLabel}] Airtable pagination wall-clock budget hit after ${elapsed}ms (page=${page}, records=${allRecords.length}); returning partial set`,
			);
			break;
		}

		const params = new URLSearchParams({ pageSize: String(pageSize) });
		if (filterByFormula) {
			params.set("filterByFormula", filterByFormula);
		}
		if (offset) {
			params.set("offset", offset);
		}

		const url = `${AIRTABLE_API_BASE}/${encodeURIComponent(baseId)}/${encodeURIComponent(tableName)}?${params.toString()}`;

		const isOffsetPage = offset !== undefined;
		// Pass a closure that re-derives remaining budget on every retry inside
		// `fetchAirtablePage`. Capturing once let `MAX_RETRY_ON_429 × RETRY_AFTER_CAP_MS`
		// of sleeps stack on top of the per-attempt timeout.
		const getRemainingBudgetMs = (): number => PAGINATION_BUDGET_MS - (Date.now() - startedAt);
		const result = await fetchAirtablePage<TFields>(
			url,
			pat,
			revalidateSeconds,
			consumerLabel,
			isOffsetPage,
			getRemainingBudgetMs,
		);
		if (!result.ok) {
			// First-page failure: throw so Next.js does NOT cache an empty
			// response. The page renders a 5xx instead of an empty directory
			// for the next revalidate window. Cause carries the triage shape
			// (`reason`, `status`, `attempts`) so APM can distinguish a
			// timeout from a rate-limit-exhaustion.
			if (!isOffsetPage) {
				throw new AirtableFirstPageError(consumerLabel, {
					reason: result.reason,
					status: result.status,
					attempts: result.attempts,
				});
			}
			// Mid-pagination failure: best-effort partial return. Pages already
			// collected stay valid; the next revalidate cycle re-fetches page 1.
			return allRecords;
		}
		allRecords.push(...result.data.records);
		offset = result.data.offset;
	} while (offset);

	return allRecords;
}
